import { ApiServiceInterface } from '../../service/api/service.interface';
import { HttpParameters } from './parameters';
import { HttpRequestInterface } from './request.interface';
import { HttpApiResponseInterface } from './api-response.interface';
import { DataKeyValueStringInterface } from '../data/key-value/string.interface';
import { HttpRequestEvent } from './request.event';
import { HttpAbortableInterface } from './abortable.interface';
import { HttpApiOptionsInterface } from './api-options.interface';

export class HttpApi implements ApiServiceInterface {
  /**
   * API remote origin (Ex: https://api.propertyfinder.ae)
   */
  public origin: string;

  /**
   *
   * @param httpRequestFactory  HttpRequest factory function
   * @param origin              API remote origin
   */
  constructor(private httpRequestFactory: () => HttpRequestInterface, origin: string) {
    this.origin = origin;
  }

  /**
   * @inheritDoc
   */
  public request(
    method: string,
    endpoint: string,
    data: object | null,
    jsonAPI: boolean = false,
    headers: DataKeyValueStringInterface = {},
    options: HttpApiOptionsInterface = {
      origin: this.origin,
      cookieUsage: true,
    }
  ): Promise<HttpApiResponseInterface> & HttpAbortableInterface {
    let httpRequest: HttpRequestInterface;
    const promise: Partial<Promise<HttpApiResponseInterface> & HttpAbortableInterface> = new Promise(
      (resolve, reject) => {
        const cookieUsage = options.cookieUsage;
        const origin = options.origin;

        // Get new request instance
        httpRequest = this.httpRequestFactory();

        // Add listeners
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.success, resolve);
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.failure, reject);
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.abort, reject);

        // Allow cookie usage
        httpRequest.setCookieUsage(cookieUsage);

        // Set common headers
        httpRequest.setHeader('Accept', 'application/vnd.api+json');
        httpRequest.setHeader('Content-Type', 'application/vnd.api+json');

        // Set additional headers
        if (Object.keys(headers).length) {
          Object.keys(headers).forEach((headerName) => {
            httpRequest.setHeader(headerName, headers[headerName]);
          });
        }

        // Set type of request
        httpRequest.setRequestType(jsonAPI);

        // Send request
        httpRequest.send(method, origin + endpoint, new HttpParameters(data));
      }
    );

    promise.abort = () => httpRequest.abort();

    return promise as Promise<HttpApiResponseInterface> & HttpAbortableInterface;
  }

  /**
   * @inheritDoc
   */
  public multipartRequest(
    method: string,
    endpoint: string,
    fieldName: string,
    file: Blob,
    headers: DataKeyValueStringInterface = {}
  ): Promise<HttpApiResponseInterface> & HttpAbortableInterface {
    let httpRequest: HttpRequestInterface;
    const promise: Partial<Promise<HttpApiResponseInterface> & HttpAbortableInterface> = new Promise(
      (resolve, reject) => {
        // Get new request instance
        httpRequest = this.httpRequestFactory();

        // Add listeners
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.success, resolve);
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.failure, reject);
        httpRequest.getEventEmitter().addListener(HttpRequestEvent.abort, reject);

        // Set additional headers
        if (Object.keys(headers).length) {
          Object.keys(headers).forEach((headerName) => {
            httpRequest.setHeader(headerName, headers[headerName]);
          });
        }

        // Send request
        httpRequest.upload(method, this.origin + endpoint, fieldName, file);
      }
    );

    promise.abort = () => httpRequest.abort();

    return promise as Promise<HttpApiResponseInterface> & HttpAbortableInterface;
  }
}
